import React, { useState, useEffect, useContext } from "react";
import { update, getDatabase, ref, set, onValue } from "firebase/database";
import { AuthContext } from "./App";
import { TheNavbar } from "./Navbar.js";
import { TheFooterFunction } from "./Footer.js";
import { Link } from "react-router-dom";
import images from "./images/profile-icon.webp";

function ProfileInfo({ title, value }) {
  return (
    <div className="row">
      <div className="col-sm-3">
        <p className="mb-0">{title}</p>
      </div>
      <div className="col-sm-9">
        <p className="text-muted mb-0">{value}</p>
      </div>
    </div>
  );
}

function EditableInfoProfile({ title, value, onChange }) {
  return (
    <div className="row mb-3">
      <div className="col-sm-3">
        <p className="mb-0">{title}</p>
      </div>
      <div className="col-sm-9">
        <textarea
          className="form-control"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        ></textarea>
      </div>
    </div>
  );
}

function EditableInfo({
  title,
  value,
  onChange,
  editable,
  alwaysEditable = false,
  onDelete,
}) {
  const isEditable = alwaysEditable || editable;

  return (
    <div className="row mb-3">
      <div className="col-sm-3">
        <p className="mb-0">{title}</p>
      </div>
      <div className="col-sm-9">
        {isEditable ? (
          <>
            <textarea
              className="form-control"
              value={value}
              onChange={(e) => onChange(e.target.value)}
            ></textarea>
            {onDelete && (
              <button className="btn btn-danger mt-2" onClick={onDelete}>
                Delete Major
              </button>
            )}
          </>
        ) : (
          <p className="text-muted mb-0">{value}</p>
        )}
      </div>
    </div>
  );
}

export function ProfilePages() {
  const { currentUser } = useContext(AuthContext);
  const [profileEditMode, setProfileEditMode] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [year, setYear] = useState("");
  const [school, setSchool] = useState("");
  const [potentialMajors, setPotentialMajors] = useState([]);
  const [editMajorsMode, setEditMajorsMode] = useState(false);
  const [editedMajors, setEditedMajors] = useState({});

  useEffect(() => {
    if (currentUser) {
      const db = getDatabase();

      const profileRef = ref(db, `profiles/${currentUser.uid}`);
      onValue(profileRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          // Set user profile info
          setFullName(data.fullName || "");
          setEmail(data.email || "");
          setPhone(data.phone || "");
          setYear(data.year || "");
          setSchool(data.school || "");

          const formattedMajors = data.potentialMajors
            ? Object.keys(data.potentialMajors).map((key) => ({
                id: key,
                ...data.potentialMajors[key],
              }))
            : [];
          setPotentialMajors(formattedMajors);
        }
      });
    }
  }, [currentUser]);

  const saveProfileToDatabase = () => {
    if (!currentUser) return;
    const db = getDatabase();
    set(ref(db, "profiles/" + currentUser.uid), {
      fullName,
      email,
      phone,
      year,
      school,
      potentialMajors,
    }).catch((error) => alert("Failed to update profile: " + error.message));
    setProfileEditMode(false);
  };

  const toggleEditMajorsMode = () => {
    setEditMajorsMode(!editMajorsMode);
  };

  const handleMajorNoteChange = (majorId, note) => {
    setEditedMajors((prevMajors) => ({
      ...prevMajors,
      [majorId]: note,
    }));
  };

  const saveMajorsToDatabase = () => {
    const db = getDatabase();
    const updates = {};
    Object.keys(editedMajors).forEach((majorId) => {
      updates[`/profiles/${currentUser.uid}/potentialMajors/${majorId}/notes`] =
        editedMajors[majorId];
    });

    update(ref(db), updates)
      .then(() => {
        const updatedMajors = potentialMajors.map((major) => {
          if (editedMajors[major.id] !== undefined) {
            return { ...major, notes: editedMajors[major.id] };
          }
          return major;
        });
        setPotentialMajors(updatedMajors);
        setEditedMajors({});
        setEditMajorsMode(false);
      })
      .catch((error) => {
        alert("Failed to update majors: " + error.message);
      });
  };

  const deleteMajor = (majorId) => {
    const updatedMajors = potentialMajors.filter(
      (major) => major.id !== majorId
    );
    setPotentialMajors(updatedMajors);
    const db = getDatabase();
    const majorRef = ref(
      db,
      `/profiles/${currentUser.uid}/potentialMajors/${majorId}`
    );
    set(majorRef, null)
      .then(() => console.log("Major deleted successfully"))
      .catch((error) => console.error("Failed to delete major: ", error));
  };

  return (
    <>
      <TheNavbar />
      <section>
        <div className="container py-5">
          <div className="row">
            <div className="col-lg-4">
              <div className="card mb-4">
                <div className="card-body text-center shadow-lg">
                  <img
                    src={images}
                    alt="Avatar"
                    className="rounded-circle img-fluid profile-image"
                  />
                  <h5 className="my-3">{fullName}</h5>
                  <p className="text-muted mb-1">{year}</p>
                  <p className="text-muted mb-4">{school}</p>
                  {profileEditMode ? (
                    <button
                      className="btn button-back"
                      onClick={saveProfileToDatabase}
                    >
                      Save Profile
                    </button>
                  ) : (

                    <button
                      className="btn button-back"
                      onClick={() => setProfileEditMode(true)}
                    >
                      Edit Profile
                    </button>

                  )}

                </div>
              </div>
            </div>
            <div className="col-lg-8">
              <div className="card mb-4">
                <div className="card-body shadow-lg">
                  {profileEditMode ? (
                    <>
                      <EditableInfoProfile
                        title="Full Name"
                        value={fullName}
                        onChange={setFullName}
                      />
                      <EditableInfoProfile
                        title="Email"
                        value={email}
                        onChange={setEmail}
                      />
                      <EditableInfoProfile
                        title="Phone"
                        value={phone}
                        onChange={setPhone}
                      />
                      <EditableInfoProfile
                        title="Year"
                        value={year}
                        onChange={setYear}
                      />
                      <EditableInfoProfile
                        title="School"
                        value={school}
                        onChange={setSchool}
                      />
                    </>
                  ) : (
                    <>
                      <ProfileInfo title="Full Name" value={fullName} />
                      <ProfileInfo title="Email" value={email} />
                      <ProfileInfo title="Phone" value={phone} />
                      <ProfileInfo title="Year" value={year} />
                      <ProfileInfo title="School" value={school} />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="col-lg-20">
            <div className="card mb-4">
              <div className="card-body shadow-lg ps-4">
                <div className="row mb-3">
                  <div className="col-sm-12 d-flex justify-content-center">
                    <h4 className="card-title mb-0">Potential Majors</h4>
                  </div>
                </div>
                {potentialMajors.length > 0 ? (
                  potentialMajors.map((major, index) => (
                    <EditableInfo
                      key={`${major.id}-${index}`}
                      title={major.name}
                      value={
                        editMajorsMode
                          ? editedMajors[major.id] || major.notes
                          : major.notes
                      }
                      onChange={
                        editMajorsMode
                          ? (note) => handleMajorNoteChange(major.id, note)
                          : null
                      }
                      editable={editMajorsMode}
                      onDelete={() => deleteMajor(major.id)}
                    />
                  ))
                ) : (
                  <div>
                    <p>
                      You have no potential majors added. Check out the{" "}
                      <Link to="/quiz">quiz</Link> to add a few!
                    </p>
                  </div>
                )}
                <div className="col-12">
                  <div className="d-flex justify-content-end mb-1">
                    {editMajorsMode ? (
                      <button
                        className="btn button-back"
                        onClick={saveMajorsToDatabase}
                      >
                        Save Majors
                      </button>
                    ) : (
                      <button
                        className="btn button-back"
                        onClick={toggleEditMajorsMode}
                      >
                        Edit Majors
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <TheFooterFunction />
    </>
  );
}
